import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  PlusIcon,
  TrashIcon,
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
  useCategories,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  useCreateSubcategory,
  useUpdateSubcategory,
  useDeleteSubcategory,
  useCreateSubSubcategory,
  useUpdateSubSubcategory,
  useDeleteSubSubcategory,
  useSetCategoryStability,
  useSetCategoryIsNeutral,
  useSetSubcategoryStability,
  useSetSubcategoryIsNeutral,
  useSetSubSubcategoryStability,
  useSetSubSubcategoryIsNeutral,
  type Category,
  type Subcategory,
  type SubSubcategory,
  type Stability,
} from "../lib/receipts";
import { useBuchungsarten, useSetDefaultSteuerArt } from "../lib/receipts";
import { OHNE_GRUPPE, gruppenRang } from "../../lib/gruppen.ts";
import { ApiError } from "../lib/api";
import { AxisOrderEditor } from "../components/AxisOrderEditor";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { SicherungStatus } from "../../lib/sicherung_status.ts";

// ── Sprach-Konventionen 2026-06-05 ─────────────────────────────────────────
// User-Wording (Replace „Stabilität" überall):
//   • Auf Leaf-Knoten (Cat ohne Subs, Sub ohne SubSubs, SubSub) heißt das Feld
//     „Kostenart-Standard" (variabel/fix) — bewusst „Standard", weil pro Beleg
//     übersteuerbar via receipts.stabilityOverride. Doppeldeutigkeit zur Sach-Cat
//     hat der User explizit in Kauf genommen (2026-06-05).
//   • Die frühere „Verwendungsart-Standard“-Auswahl der Top-Cat ist entfernt (18.10.2026) — die Steuer-Achse ist die Buchungsart.
//   • Beleg-Toggle (StabilityInlineToggle) heißt einfach „Kostenart" (gilt für
//     diesen einen Beleg, ohne -Standard-Suffix).
//
// Hierarchie-Konvention (Migration 0012):
//   • Stability + isNeutral sitzen auf dem LEAF — auf Non-Leaf-Knoten kein Toggle.
//   • Settings-UI: 4-Ebenen-Akkordion Gruppe (der Buchungsart-Standards) › Top-Cat › Sub › Leaf-Card (S3; Verwendungsart entfernt 18.10.).

// iOS-Zoom-Fix: 16px auf Mobile verhindert Auto-Zoom beim Fokus, 14px ab lg.
const inputCls =
  "w-full rounded-ios border border-ios-line bg-white px-3 py-2 text-[16px] lg:text-[14px] focus:outline-none focus:ring-2 focus:ring-ios-blue/40";

const KEIN_STANDARD = "__kein_standard__";
const GEMISCHT = "__gemischt__";

function describeError(e: unknown): string {
  if (e instanceof ApiError) {
    const p = e.payload;
    if (e.status === 409 && p && typeof p === "object" && "reason" in (p as Record<string, unknown>)) {
      return String((p as Record<string, unknown>).reason);
    }
    return e.message;
  }
  return "Unbekannter Fehler";
}

export const EinstellungenPage = () => {
  const { data, isLoading } = useCategories();
  const [err, setErr] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [newCat, setNewCat] = useState("");
  const createCat = useCreateCategory();

  const onError = (e: unknown) => setErr(describeError(e));

  const cats = useMemo(
    () => [...(data?.categories ?? [])].sort((a, b) => a.name.localeCompare(b.name, "de")),
    [data?.categories],
  );
  const subs = useMemo(
    () => [...(data?.subcategories ?? [])].sort((a, b) => a.name.localeCompare(b.name, "de")),
    [data?.subcategories],
  );
  const sub2s = useMemo(
    () => [...(data?.subSubcategories ?? [])].sort((a, b) => a.name.localeCompare(b.name, "de")),
    [data?.subSubcategories],
  );

  const active = cats.filter((c) => !c.archived);
  const archived = cats.filter((c) => c.archived);

  // Leaf-Liste über alle 3 Ebenen, um zu zählen, wie viele Leafs noch keine
  // Kostenart-Standard gesetzt haben (Migration-Banner-Hinweis).
  const leafCountsMissingStandard = useMemo(() => {
    let missing = 0;
    for (const c of active) {
      const subsOfCat = subs.filter((s) => s.parentId === c.id && !s.archived);
      if (subsOfCat.length === 0) {
        if (!c.stability) missing += 1;
        continue;
      }
      for (const s of subsOfCat) {
        const ssOfSub = sub2s.filter((ss) => ss.parentId === s.id && !ss.archived);
        if (ssOfSub.length === 0) {
          if (!s.stability) missing += 1;
          continue;
        }
        for (const ss of ssOfSub) {
          if (!ss.stability) missing += 1;
        }
      }
    }
    return missing;
  }, [active, subs, sub2s]);

  // Oberflächen-Paket S3: oberste Ebene = Gruppe der Buchungsart-Standards im Strang (statt der alten Verwendungsart).
  // Je End-Kategorie gilt der eigene Standard, sonst der geerbte (Sub-Sub → Sub → Kategorie). Liegen alle End-Kategorien
  // einer Kategorie in derselben Gruppe → diese Gruppe; mehrere → „Gemischt“; kein Standard irgendwo → „Ohne Standard“.
  const { data: artDaten } = useBuchungsarten("standard");
  const groups: { key: string; label: string; cats: Category[] }[] = useMemo(() => {
    const gruppeVonArt = new Map((artDaten?.arten ?? []).map((a) => [a.id, a.gruppe ?? OHNE_GRUPPE]));
    const gruppenDerKategorie = (c: Category): Set<string> => {
      const g = new Set<string>();
      const nimm = (artId: string | null | undefined) => g.add(artId ? gruppeVonArt.get(artId) ?? OHNE_GRUPPE : KEIN_STANDARD);
      const subsOfCat = subs.filter((s) => s.parentId === c.id && !s.archived);
      if (subsOfCat.length === 0) nimm(c.defaultSteuerArtId);
      for (const s of subsOfCat) {
        const ssOfSub = sub2s.filter((ss) => ss.parentId === s.id && !ss.archived);
        if (ssOfSub.length === 0) nimm(s.defaultSteuerArtId ?? c.defaultSteuerArtId);
        for (const ss of ssOfSub) nimm(ss.defaultSteuerArtId ?? s.defaultSteuerArtId ?? c.defaultSteuerArtId);
      }
      return g;
    };
    const byGruppe = new Map<string, Category[]>();
    for (const c of active) {
      const g = gruppenDerKategorie(c);
      const ohneLeer = [...g].filter((x) => x !== KEIN_STANDARD);
      const k = ohneLeer.length === 0 ? KEIN_STANDARD : g.size === 1 ? ohneLeer[0]! : GEMISCHT;
      byGruppe.set(k, [...(byGruppe.get(k) ?? []), c]);
    }
    const rang = (k: string) => (k === GEMISCHT ? 90 : k === KEIN_STANDARD ? 91 : gruppenRang(k));
    return [...byGruppe.entries()]
      .sort((a, b) => rang(a[0]) - rang(b[0]) || a[0].localeCompare(b[0], "de"))
      .map(([k, cats]) => ({
        key: k,
        label: k === GEMISCHT ? "Gemischt (mehrere Gruppen oder teils ohne Standard)" : k === KEIN_STANDARD ? "Ohne Buchungsart-Standard" : k,
        cats,
      }));
  }, [active, subs, sub2s, artDaten]);

  // Klapp-Zustände (Top-Akkordion). Default: alle Gruppen offen.
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const addCategory = () => {
    const name = newCat.trim();
    if (!name) return;
    setErr(null);
    createCat.mutate({ name }, { onSuccess: () => setNewCat(""), onError });
  };

  return (
    <div className="px-5 py-8 md:px-10 md:py-10 max-w-3xl mx-auto animate-fade-in">
      <header className="mb-6">
        <h1 className="text-[28px] md:text-[34px] font-semibold tracking-tight">Einstellungen</h1>
        <p className="text-[15px] text-ios-muted mt-1">Kategorien &amp; Unterkategorien verwalten</p>
      </header>

      {err && (
        <div className="ios-card border border-ios-red/30 bg-ios-red/5 p-3 mb-4 flex items-start justify-between gap-3">
          <span className="text-[14px] text-ios-red">{err}</span>
          <button onClick={() => setErr(null)} className="text-ios-red shrink-0" aria-label="Schließen">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {leafCountsMissingStandard > 0 && (
        <div className="ios-card border border-amber-400/40 bg-amber-50 p-3 mb-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-[14px] text-amber-900">
            <strong>{leafCountsMissingStandard}</strong>{" "}
            {leafCountsMissingStandard === 1 ? "End-Kategorie hat" : "End-Kategorien haben"} noch
            keinen <em>Kostenart-Standard</em> (variabel/fix). Belege ohne Standard landen im
            Tree-Bucket „⚠️ ohne Kostenart-Standard".
          </div>
        </div>
      )}

      <SicherungAbschnitt />

      <section className="mb-6">
        <h2 className="text-[13px] font-medium text-ios-subtle uppercase tracking-wide mb-2 px-1">Beleg-Eingang</h2>
        <Link to="/einstellungen/karten" className="flex items-center justify-between bg-white rounded-iosLg shadow-card px-4 py-3">
          <span>
            <span className="block text-[15px] font-medium">Karten</span>
            <span className="block text-[13px] text-ios-muted">Endziffern auf Bons → Bereich bestätigen</span>
          </span>
          <ChevronRightIcon className="w-5 h-5 text-ios-muted" />
        </Link>
        <Link to="/einstellungen/vertraege" className="mt-2 flex items-center justify-between bg-white rounded-iosLg shadow-card px-4 py-3">
          <span>
            <span className="block text-[15px] font-medium">Verträge</span>
            <span className="block text-[13px] text-ios-muted">Rechnungen an der Kundennummer erkennen → automatisch aufteilen</span>
          </span>
          <ChevronRightIcon className="w-5 h-5 text-ios-muted" />
        </Link>
        <Link to="/einstellungen/haendler" className="mt-2 flex items-center justify-between bg-white rounded-iosLg shadow-card px-4 py-3">
          <span>
            <span className="block text-[15px] font-medium">Händler</span>
            <span className="block text-[13px] text-ios-muted">Rechtsform einmal je Firma festlegen, Schreibweisen vereinheitlichen</span>
          </span>
          <ChevronRightIcon className="w-5 h-5 text-ios-muted" />
        </Link>
      </section>
      <section className="mb-6">
        <h2 className="text-[13px] font-medium text-ios-subtle uppercase tracking-wide mb-2 px-1">Buchungen</h2>
        <Link to="/einstellungen/konten" className="flex items-center justify-between bg-white rounded-iosLg shadow-card px-4 py-3">
          <span>
            <span className="block text-[15px] font-medium">Konten</span>
            <span className="block text-[13px] text-ios-muted">Kontoauszug-Format je Konto · Kontoauszüge importieren</span>
          </span>
          <ChevronRightIcon className="w-5 h-5 text-ios-muted" />
        </Link>
        <Link to="/einstellungen/jahreswerte" className="mt-2 flex items-center justify-between bg-white rounded-iosLg shadow-card px-4 py-3">
          <span>
            <span className="block text-[15px] font-medium">Jahreswerte</span>
            <span className="block text-[13px] text-ios-muted">Unterlagen, Flächen, Gesetzeswerte · Rollen der Buchungsarten · Anlagegüter</span>
          </span>
          <ChevronRightIcon className="w-5 h-5 text-ios-muted" />
        </Link>
      </section>

      <section>
        <h2 className="text-[13px] font-medium text-ios-subtle uppercase tracking-wide mb-2 px-1">Kategorien</h2>

        <div className="ios-card p-3 mb-4 flex items-center gap-2">
          <input
            className={inputCls}
            placeholder="Neue Kategorie …"
            value={newCat}
            onChange={(e) => setNewCat(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCategory()}
          />
          <button
            onClick={addCategory}
            disabled={!newCat.trim() || createCat.isPending}
            className="shrink-0 inline-flex items-center gap-1 rounded-ios bg-ios-blue text-white px-3 py-2 text-[14px] font-medium disabled:opacity-40"
          >
            <PlusIcon className="w-4 h-4" /> Hinzufügen
          </button>
        </div>

        {isLoading ? (
          <p className="text-[15px] text-ios-muted px-1">Lädt …</p>
        ) : active.length === 0 ? (
          <p className="text-[15px] text-ios-muted px-1">Noch keine Kategorien angelegt.</p>
        ) : (
          <div className="space-y-3">
            {groups.map((g) => (
              <UsageGroup
                key={g.key}
                label={g.label}
                cats={g.cats}
                subs={subs}
                sub2s={sub2s}
                isOpen={openGroups[g.key] ?? true}
                onToggleOpen={() => setOpenGroups((s) => ({ ...s, [g.key]: !(s[g.key] ?? true) }))}
                onError={onError}
                clearError={() => setErr(null)}
              />
            ))}
          </div>
        )}

        {archived.length > 0 && (
          <div className="mt-6">
            <button
              onClick={() => setShowArchived((v) => !v)}
              className="text-[13px] text-ios-subtle hover:text-ios-muted px-1"
            >
              {showArchived ? "▾" : "▸"} Archiviert ({archived.length})
            </button>
            {showArchived && (
              <div className="space-y-3 mt-2 opacity-70">
                {archived.map((cat) => (
                  <CategoryCard
                    key={cat.id}
                    cat={cat}
                    subs={subs.filter((s) => s.parentId === cat.id)}
                    sub2s={sub2s}
                    onError={onError}
                    clearError={() => setErr(null)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      <p className="text-[13px] text-ios-subtle mt-8 px-1">
        Tipp: „Archivieren" blendet eine Kategorie aus der Auswahl aus, ohne bestehende
        Zuordnungen zu verlieren. „Löschen" geht nur, wenn sie nirgends verwendet wird.
        Vendor-Aliases, MwSt-Regeln und OCR-Modus folgen später.
      </p>

      {/* Migration 0010: Achsen-Reihenfolge im Tree (User-konfigurierbar) */}
      <section className="mt-8">
        <h2 className="text-[17px] font-semibold tracking-tight mb-3 px-1">Navigation</h2>
        <div className="ios-card p-4">
          <AxisOrderEditor />
        </div>
      </section>
    </div>
  );
};

// ── Gruppe (oberster Akkordion-Knoten) ──────────────────────────────

function UsageGroup({
  label,
  cats,
  subs,
  sub2s,
  isOpen,
  onToggleOpen,
  onError,
  clearError,
}: {
  label: string;
  cats: Category[];
  subs: Subcategory[];
  sub2s: SubSubcategory[];
  isOpen: boolean;
  onToggleOpen: () => void;
  onError: (e: unknown) => void;
  clearError: () => void;
}) {
  return (
    <div className="ios-card overflow-hidden">
      <button
        onClick={onToggleOpen}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-ios-bg/40 transition-colors"
      >
        {isOpen ? <ChevronDownIcon className="w-4 h-4 text-ios-subtle" /> : <ChevronRightIcon className="w-4 h-4 text-ios-subtle" />}
        <span className="text-[15px] font-semibold tracking-tight">{label}</span>
        <span className="text-[12px] text-ios-subtle">({cats.length})</span>
      </button>
      {isOpen && (
        <div className="px-3 pb-3 space-y-2">
          {cats.map((cat) => (
            <CategoryCard
              key={cat.id}
              cat={cat}
              subs={subs.filter((s) => s.parentId === cat.id)}
              sub2s={sub2s}
              onError={onError}
              clearError={clearError}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Top-Cat-Card ──────────────────────────────────────────────────────────────
// Two-Shape:
//   1. Leaf (keine aktiven Subs) → Kostenart-Standard + Neutral direkt im Card
//   2. Non-Leaf (mit Subs)       → nur Top-Cat-Optionen (Buchungsart-Standard);
//                                  Stabilität/Neutral wandern auf Subs/SubSubs.

function CategoryCard({
  cat,
  subs,
  sub2s,
  onError,
  clearError,
}: {
  cat: Category;
  subs: Subcategory[];
  sub2s: SubSubcategory[];
  onError: (e: unknown) => void;
  clearError: () => void;
}) {
  const updateCat = useUpdateCategory();
  const deleteCat = useDeleteCategory();
  const createSub = useCreateSubcategory();
  const setStability = useSetCategoryStability();
  const setIsNeutral = useSetCategoryIsNeutral();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(cat.name);
  const [newSub, setNewSub] = useState("");
  const [collapsed, setCollapsed] = useState(false);

  const activeSubs = subs.filter((s) => !s.archived);
  const isLeaf = activeSubs.length === 0;

  const saveName = () => {
    const n = name.trim();
    if (!n || n === cat.name) {
      setEditing(false);
      return;
    }
    clearError();
    updateCat.mutate({ id: cat.id, patch: { name: n } }, { onSuccess: () => setEditing(false), onError });
  };

  const addSub = () => {
    const n = newSub.trim();
    if (!n) return;
    clearError();
    createSub.mutate({ parentId: cat.id, name: n }, { onSuccess: () => setNewSub(""), onError });
  };

  return (
    <div className="rounded-ios border border-ios-line/60 bg-white">
      <div className="p-3">
        <div className="flex items-center gap-2">
          {!isLeaf && (
            <button
              onClick={() => setCollapsed((v) => !v)}
              className="text-ios-subtle shrink-0"
              aria-label={collapsed ? "Aufklappen" : "Zuklappen"}
            >
              {collapsed ? <ChevronRightIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
            </button>
          )}
          {cat.sfSymbolOrEmoji && <span className="text-[18px]">{cat.sfSymbolOrEmoji}</span>}
          {editing ? (
            <>
              <input
                className={inputCls}
                value={name}
                autoFocus
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveName();
                  if (e.key === "Escape") {
                    setName(cat.name);
                    setEditing(false);
                  }
                }}
              />
              <button onClick={saveName} className="text-ios-blue shrink-0" aria-label="Speichern">
                <CheckIcon className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  setName(cat.name);
                  setEditing(false);
                }}
                className="text-ios-muted shrink-0"
                aria-label="Abbrechen"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <span className="flex-1 text-[16px] font-medium">{cat.name}</span>
              {!isLeaf && (
                <span className="text-[11px] text-ios-subtle shrink-0">
                  {activeSubs.length} {activeSubs.length === 1 ? "Sub" : "Subs"}
                </span>
              )}
              <button
                onClick={() => {
                  setName(cat.name);
                  setEditing(true);
                }}
                title="Umbenennen"
                className="text-ios-subtle hover:text-ios-blue shrink-0"
              >
                <PencilSquareIcon className="w-5 h-5" />
              </button>
              {cat.archived ? (
                <button
                  onClick={() => {
                    clearError();
                    updateCat.mutate({ id: cat.id, patch: { archived: false } }, { onError });
                  }}
                  title="Reaktivieren"
                  className="text-ios-subtle hover:text-ios-blue shrink-0"
                >
                  <ArrowUturnLeftIcon className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={() => {
                    clearError();
                    updateCat.mutate({ id: cat.id, patch: { archived: true } }, { onError });
                  }}
                  title="Archivieren"
                  className="text-ios-subtle hover:text-ios-blue shrink-0"
                >
                  <ArchiveBoxIcon className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={() => {
                  if (activeSubs.length > 0) return;
                  clearError();
                  deleteCat.mutate(cat.id, { onError });
                }}
                disabled={activeSubs.length > 0}
                title={
                  activeSubs.length > 0
                    ? `${activeSubs.length} ${activeSubs.length === 1 ? "Subkategorie" : "Subkategorien"} — erst entfernen/archivieren`
                    : "Löschen (nur wenn nirgends verwendet — sonst archivieren)"
                }
                className="text-ios-subtle hover:text-ios-red disabled:hover:text-ios-subtle disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </>
          )}
        </div>

        {/* Oberflächen-Paket: Buchungsart-Standard auf ALLEN Ebenen (nicht nur am Blatt). */}
        <BuchungsartStandard
          ebene="categories"
          id={cat.id}
          wert={cat.defaultSteuerArtId ?? null}
          erbt={null}
          erben={activeSubs.filter((s) => !s.defaultSteuerArtId).map((s) => s.name)}
          onError={onError}
          clearError={clearError}
        />

        {/* Wenn Leaf (keine Subs) → Kostenart-Standard + Neutral direkt auf Top-Cat */}
        {isLeaf && (
          <LeafToggles
            stability={cat.stability ?? null}
            isNeutral={cat.isNeutral === true}
            pendingStab={setStability.isPending}
            pendingNeu={setIsNeutral.isPending}
            onStabilityChange={(v) => {
              clearError();
              setStability.mutate({ id: cat.id, stability: v }, { onError });
            }}
            onIsNeutralChange={(v) => {
              clearError();
              setIsNeutral.mutate({ id: cat.id, isNeutral: v }, { onError });
            }}
          />
        )}
      </div>

      {/* Sub-Liste (nur wenn Non-Leaf) */}
      {!isLeaf && !collapsed && (
        <div className="border-t border-ios-line/40 px-3 py-2 space-y-1">
          {activeSubs.map((s) => (
            <SubcategoryRow
              key={s.id}
              sub={s}
              erbt={cat.defaultSteuerArtId ? { artId: cat.defaultSteuerArtId, von: cat.name } : null}
              sub2s={sub2s.filter((s2) => s2.parentId === s.id && !s2.archived)}
              onError={onError}
              clearError={clearError}
            />
          ))}
          {/* Archivierte Subs werden hier ausgeblendet — sichtbar nur in der „Archiviert"-Toggle der Page */}
          <div className="flex items-center gap-2 pt-1">
            <input
              className={inputCls + " !py-1"}
              placeholder="+ Unterkategorie"
              value={newSub}
              onChange={(e) => setNewSub(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addSub()}
            />
            {newSub.trim() && (
              <button onClick={addSub} className="text-ios-blue shrink-0" aria-label="Unterkategorie hinzufügen">
                <PlusIcon className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-Row ───────────────────────────────────────────────────────────────────
// Two-Shape:
//   1. Leaf (keine SubSubs) → Kostenart-Standard + Neutral
//   2. Non-Leaf              → Sub-Subs darunter; selbst KEIN Toggle

function SubcategoryRow({
  sub,
  sub2s,
  erbt,
  onError,
  clearError,
}: {
  erbt: { artId: string; von: string } | null;
  sub: Subcategory;
  sub2s: SubSubcategory[];
  onError: (e: unknown) => void;
  clearError: () => void;
}) {
  const updateSub = useUpdateSubcategory();
  const deleteSub = useDeleteSubcategory();
  const createSub2 = useCreateSubSubcategory();
  const setStab = useSetSubcategoryStability();
  const setNeu = useSetSubcategoryIsNeutral();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(sub.name);
  const [newSub2, setNewSub2] = useState("");
  const [showAddSub2, setShowAddSub2] = useState(false);

  const isLeaf = sub2s.length === 0;

  const save = () => {
    const n = name.trim();
    if (!n || n === sub.name) {
      setEditing(false);
      return;
    }
    clearError();
    updateSub.mutate({ id: sub.id, patch: { name: n } }, { onSuccess: () => setEditing(false), onError });
  };

  const addSub2 = () => {
    const n = newSub2.trim();
    if (!n) return;
    clearError();
    createSub2.mutate(
      { parentId: sub.id, name: n },
      {
        onSuccess: () => {
          setNewSub2("");
          setShowAddSub2(false);
        },
        onError,
      },
    );
  };

  return (
    <div>
      <div className={"flex items-center gap-2 " + (sub.archived ? "opacity-50" : "")}>
        {editing ? (
          <>
            <input
              className={inputCls + " !py-1"}
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") {
                  setName(sub.name);
                  setEditing(false);
                }
              }}
            />
            <button onClick={save} className="text-ios-blue shrink-0" aria-label="Speichern">
              <CheckIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setName(sub.name);
                setEditing(false);
              }}
              className="text-ios-muted shrink-0"
              aria-label="Abbrechen"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <span className="flex-1 text-[14px] text-ios-ink">
              {sub.name}
              {sub.archived && " (archiviert)"}
            </span>
            <button
              onClick={() => {
                setName(sub.name);
                setEditing(true);
              }}
              title="Umbenennen"
              className="text-ios-subtle hover:text-ios-blue shrink-0"
            >
              <PencilSquareIcon className="w-4 h-4" />
            </button>
            {sub.archived ? (
              <button
                onClick={() => {
                  clearError();
                  updateSub.mutate({ id: sub.id, patch: { archived: false } }, { onError });
                }}
                title="Reaktivieren"
                className="text-ios-subtle hover:text-ios-blue shrink-0"
              >
                <ArrowUturnLeftIcon className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => {
                  clearError();
                  updateSub.mutate({ id: sub.id, patch: { archived: true } }, { onError });
                }}
                title="Archivieren"
                className="text-ios-subtle hover:text-ios-blue shrink-0"
              >
                <ArchiveBoxIcon className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setShowAddSub2((v) => !v)}
              title="Sub-Sub-Kategorie hinzufügen"
              className="text-ios-subtle hover:text-ios-blue shrink-0"
            >
              <PlusIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                if (sub2s.length > 0) return;
                clearError();
                deleteSub.mutate(sub.id, { onError });
              }}
              disabled={sub2s.length > 0}
              title={
                sub2s.length > 0
                  ? `${sub2s.length} Sub-Sub-${sub2s.length === 1 ? "Kategorie" : "Kategorien"} — erst entfernen/archivieren`
                  : "Löschen (nur wenn nirgends verwendet — sonst archivieren)"
              }
              className="text-ios-subtle hover:text-ios-red disabled:hover:text-ios-subtle disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {!sub.archived && (
        <BuchungsartStandard
          compact
          ebene="subcategories"
          id={sub.id}
          wert={sub.defaultSteuerArtId ?? null}
          erbt={erbt}
          erben={sub2s.filter((s2) => !s2.defaultSteuerArtId).map((s2) => s2.name)}
          onError={onError}
          clearError={clearError}
        />
      )}

      {/* Leaf-Sub (keine SubSubs) bekommt direkt Toggles */}
      {isLeaf && !sub.archived && (
        <LeafToggles
          compact
          stability={sub.stability ?? null}
          isNeutral={sub.isNeutral === true}
          pendingStab={setStab.isPending}
          pendingNeu={setNeu.isPending}
          onStabilityChange={(v) => {
            clearError();
            setStab.mutate({ id: sub.id, stability: v }, { onError });
          }}
          onIsNeutralChange={(v) => {
            clearError();
            setNeu.mutate({ id: sub.id, isNeutral: v }, { onError });
          }}
        />
      )}

      {/* Non-Leaf-Sub → SubSubs darunter */}
      {(sub2s.length > 0 || showAddSub2) && (
        <div className="ml-4 pl-3 border-l border-ios-line/60 space-y-1 mt-1">
          {sub2s.map((s2) => (
            <SubSubcategoryRow key={s2.id} sub2={s2} onError={onError} clearError={clearError}
              erbt={sub.defaultSteuerArtId ? { artId: sub.defaultSteuerArtId, von: sub.name } : erbt} />
          ))}
          {showAddSub2 && (
            <div className="flex items-center gap-2 pt-1">
              <input
                className={inputCls + " !py-1 text-[13px]"}
                placeholder="+ Sub-Sub-Kategorie"
                value={newSub2}
                autoFocus
                onChange={(e) => setNewSub2(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") addSub2();
                  if (e.key === "Escape") {
                    setNewSub2("");
                    setShowAddSub2(false);
                  }
                }}
              />
              <button
                onClick={addSub2}
                disabled={!newSub2.trim()}
                className="text-ios-blue shrink-0 disabled:opacity-40"
                aria-label="Hinzufügen"
              >
                <CheckIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => {
                  setNewSub2("");
                  setShowAddSub2(false);
                }}
                className="text-ios-muted shrink-0"
                aria-label="Abbrechen"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── SubSub-Row (immer Leaf — bekommt Kostenart-Standard + Neutral) ────────────

function SubSubcategoryRow({
  sub2,
  erbt,
  onError,
  clearError,
}: {
  erbt: { artId: string; von: string } | null;
  sub2: SubSubcategory;
  onError: (e: unknown) => void;
  clearError: () => void;
}) {
  const updateSub2 = useUpdateSubSubcategory();
  const deleteSub2 = useDeleteSubSubcategory();
  const setStab = useSetSubSubcategoryStability();
  const setNeu = useSetSubSubcategoryIsNeutral();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(sub2.name);

  const save = () => {
    const n = name.trim();
    if (!n || n === sub2.name) {
      setEditing(false);
      return;
    }
    clearError();
    updateSub2.mutate({ id: sub2.id, patch: { name: n } }, { onSuccess: () => setEditing(false), onError });
  };

  return (
    <div>
      <div className={"flex items-center gap-2 " + (sub2.archived ? "opacity-50" : "")}>
        {editing ? (
          <>
            <input
              className={inputCls + " !py-1 text-[13px]"}
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                if (e.key === "Escape") {
                  setName(sub2.name);
                  setEditing(false);
                }
              }}
            />
            <button onClick={save} className="text-ios-blue shrink-0" aria-label="Speichern">
              <CheckIcon className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setName(sub2.name);
                setEditing(false);
              }}
              className="text-ios-muted shrink-0"
              aria-label="Abbrechen"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </>
        ) : (
          <>
            <span className="flex-1 text-[13px] text-ios-ink">
              ↳ {sub2.name}
              {sub2.archived && " (archiviert)"}
            </span>
            <button
              onClick={() => {
                setName(sub2.name);
                setEditing(true);
              }}
              title="Umbenennen"
              className="text-ios-subtle hover:text-ios-blue shrink-0"
            >
              <PencilSquareIcon className="w-3.5 h-3.5" />
            </button>
            {sub2.archived ? (
              <button
                onClick={() => {
                  clearError();
                  updateSub2.mutate({ id: sub2.id, patch: { archived: false } }, { onError });
                }}
                title="Reaktivieren"
                className="text-ios-subtle hover:text-ios-blue shrink-0"
              >
                <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
              </button>
            ) : (
              <button
                onClick={() => {
                  clearError();
                  updateSub2.mutate({ id: sub2.id, patch: { archived: true } }, { onError });
                }}
                title="Archivieren"
                className="text-ios-subtle hover:text-ios-blue shrink-0"
              >
                <ArchiveBoxIcon className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={() => {
                clearError();
                deleteSub2.mutate(sub2.id, { onError });
              }}
              title="Löschen (nur wenn unbenutzt)"
              className="text-ios-subtle hover:text-ios-red shrink-0"
            >
              <TrashIcon className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
      {!sub2.archived && (
        <BuchungsartStandard compact ebene="sub-subcategories" id={sub2.id} wert={sub2.defaultSteuerArtId ?? null}
          erbt={erbt} erben={[]} onError={onError} clearError={clearError} />
      )}
      {!sub2.archived && (
        <LeafToggles
          compact
          stability={sub2.stability ?? null}
          isNeutral={sub2.isNeutral === true}
          pendingStab={setStab.isPending}
          pendingNeu={setNeu.isPending}
          onStabilityChange={(v) => {
            clearError();
            setStab.mutate({ id: sub2.id, stability: v }, { onError });
          }}
          onIsNeutralChange={(v) => {
            clearError();
            setNeu.mutate({ id: sub2.id, isNeutral: v }, { onError });
          }}
        />
      )}
    </div>
  );
}

// ── Leaf-Toggles: Pillen-Stil für Kostenart-Standard + Neutral ────────────────
// Konsistente UI für alle drei Leaf-Ebenen (Cat-Leaf, Sub-Leaf, SubSub).

function LeafToggles({
  stability,
  isNeutral,
  pendingStab,
  pendingNeu,
  onStabilityChange,
  onIsNeutralChange,
  compact = false,
}: {
  stability: Stability | null;
  isNeutral: boolean;
  pendingStab: boolean;
  pendingNeu: boolean;
  onStabilityChange: (v: Stability | null) => void;
  onIsNeutralChange: (v: boolean) => void;
  compact?: boolean;
}) {
  const sizeCls = compact ? "text-[11px] px-2 py-0.5" : "text-[12px] px-2.5 py-1";
  const labelSize = compact ? "text-[12px]" : "text-[13px]";
  const Pill = ({
    active,
    onClick,
    children,
    title,
  }: {
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
    title?: string;
  }) => (
    <button
      onClick={onClick}
      title={title}
      className={`${sizeCls} rounded-full border transition-colors ${
        active
          ? "bg-ios-blue text-white border-ios-blue"
          : "bg-white text-ios-ink border-ios-line hover:border-ios-blue/60"
      }`}
    >
      {children}
    </button>
  );
  return (
    <div className={`${compact ? "mt-1 ml-1" : "mt-2 pl-1"} flex flex-wrap items-center gap-x-3 gap-y-1.5 ${labelSize} text-ios-muted`}>
      <div className="flex items-center gap-1.5">
        <span className="shrink-0">Kostenart-Standard:</span>
        <Pill active={stability === "fixed"} onClick={() => onStabilityChange(stability === "fixed" ? null : "fixed")} title="Fixe Kosten (z.B. Miete, ADAC)">
          Fix
        </Pill>
        <Pill active={stability === "variable"} onClick={() => onStabilityChange(stability === "variable" ? null : "variable")} title="Variable Kosten (z.B. Strom, Lebensmittel)">
          Variabel
        </Pill>
        {!stability && (
          <span className="text-amber-700 text-[11px]" title="Diese End-Kategorie hat keinen Kostenart-Standard.">
            ⚠️
          </span>
        )}
        {pendingStab && <span className="text-ios-subtle text-[10px]">…</span>}
      </div>
      <Pill
        active={isNeutral}
        onClick={() => onIsNeutralChange(!isNeutral)}
        title="Saldenverschiebung (Tilgung, Umbuchung) — wird in Reports als 3. Σ-Bucket geführt, nicht als Einnahme/Ausgabe."
      >
        <span aria-hidden="true">↔ </span>Neutral
      </Pill>
      {pendingNeu && <span className="text-ios-subtle text-[10px]">…</span>}
    </div>
  );
}

// ── Oberflächen-Paket: Buchungsart-Standard je Ebene ─────────────────────────────────────────────────────────────
// Leer = erbt den Standard der oberen Ebene (angezeigt als „erbt: X (von Y)“). Ein Standard oben sickert in jede
// Unterebene ohne eigenen Standard durch — deshalb steht dabei, welche Unterebenen ihn erben.
// Änderungen wirken NICHT rückwirkend (Modell B): nur für neue Positionen und beim Kategoriewechsel.
function BuchungsartStandard({ ebene, id, wert, erbt, erben, compact, onError, clearError }: {
  ebene: "categories" | "subcategories" | "sub-subcategories";
  id: string;
  wert: string | null;
  erbt: { artId: string; von: string } | null;
  erben: string[];
  compact?: boolean;
  onError: (e: unknown) => void;
  clearError: () => void;
}) {
  const { data } = useBuchungsarten("standard");
  const setze = useSetDefaultSteuerArt();
  const arten = data?.arten ?? [];
  const name = (artId: string) => arten.find((a) => a.id === artId)?.name ?? "…";
  return (
    <div className={`mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 ${compact ? "text-[12px]" : "text-[13px]"} text-ios-muted pl-1`}>
      <span className="shrink-0">Buchungsart-Standard:</span>
      <select
        className="bg-ios-bg/60 rounded-ios px-1.5 py-0.5 text-[16px] lg:text-[13px] text-ios-ink max-w-full"
        value={wert ?? ""}
        disabled={setze.isPending}
        onChange={(e) => {
          clearError();
          setze.mutate({ ebene, id, steuerArtId: e.target.value || null }, { onError });
        }}
      >
        <option value="">{erbt ? `— erbt: ${name(erbt.artId)} (von ${erbt.von})` : "— keiner (Buchungsart beim Prüfen wählen)"}</option>
        {arten.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
      </select>
      {wert && erben.length > 0 && (
        <span className="basis-full text-[11px]">gilt auch für: {erben.join(", ")} (ohne eigenen Standard)</span>
      )}
      {!compact && (
        <span className="basis-full text-[11px]">Wirkt nur für neue Positionen und beim Kategoriewechsel — vorhandene Belege bleiben unverändert.</span>
      )}
    </div>
  );
}

/** A3 (28.10.2026): Stand der Sicherungskette — Text + Farbe (grün = in Ordnung, blau = offen/zu alt; nie nur Farbe). */
function SicherungAbschnitt() {
  const { data } = useQuery<SicherungStatus>({ queryKey: ["sicherung"], queryFn: () => api.get("/api/sicherung") });
  const zeit = (iso: string | null) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : null);
  const zeile = (titel: string, ok: boolean, text: string) => (
    <div className="flex items-start justify-between gap-3 px-4 py-3">
      <span className="text-[15px] font-medium">{titel}</span>
      <span className={`text-[14px] text-right ${ok ? "text-ios-green" : "text-ios-blue"}`}>{ok ? "✓ " : "⚠ "}{text}</span>
    </div>
  );
  return (
    <section className="mb-6">
      <h2 className="text-[13px] font-medium text-ios-subtle uppercase tracking-wide mb-2 px-1">Sicherung</h2>
      <div className="bg-white rounded-iosLg shadow-card divide-y divide-ios-line/60">
        {!data ? <div className="px-4 py-3 text-[14px] text-ios-muted">wird geladen …</div> : (<>
          {zeile("Server-Sicherung (alle 15 Minuten)", data.server.inOrdnung, data.server.zeit
            ? `zuletzt ${zeit(data.server.zeit)} (vor ${data.server.alterMin} min)${data.server.inOrdnung ? "" : " — älter als erwartet"}`
            : "noch keine Meldung")}
          {zeile("Abholung durch den Mac (stündlich)", data.mac.inOrdnung, data.mac.zeit
            ? `zuletzt ${zeit(data.mac.zeit)} (vor ${data.mac.alterStd} h) · ${data.mac.belege ?? "?"} Belege, ${data.mac.dokumente ?? "?"} Dokumente`
              + (data.mac.fehlend ? ` · ${data.mac.fehlend} fehlen in der Mac-Kopie` : "") + (data.mac.inOrdnung ? "" : (data.mac.fehlend ? "" : " — älter als erwartet"))
            : "noch keine Meldung")}
        </>)}
      </div>
    </section>
  );
}
